"use client";
import { useEffect, Suspense } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { useZStore } from "@/utils/z-store";
import cn from "@/utils/cn";
import {
  CreateAccountFormModal,
  DashboardIntro,
  DashboardWelcomeModal,
  JoinWaitlistModal,
  MailchimpAPISettingsModal,
  SignInModal,
  ReportBuilderModal,
  ForgotPasswordModal,
  ResetPasswordModal,
  InviteAccessModal,
  OverwriteTemplateModal,
  DeleteAccountModal,
} from "./modals";
import InvitationModal from "./modals/InvitationModal";
import ManageConsentModal from "./modals/ManageConsentModal";
import BillingModalContent from "../(payment)/payment/page";

interface ModalProps {
  className?: string;
  inviteToken?: string;
}

const modalComponents: any = {
  "start-free-trial": JoinWaitlistModal,
  "sign-up": CreateAccountFormModal,
  "sign-in": SignInModal,
  "dashboard-welcome": DashboardWelcomeModal,
  "mailchimp-api-key": MailchimpAPISettingsModal,
  "report-builder": ReportBuilderModal,
  "dashboard-intro": DashboardIntro,
  "invite-user": InvitationModal,
  "manage-consent": ManageConsentModal,
  "forgot-password": ForgotPasswordModal,
  "reset-password": ResetPasswordModal,
  "invite-access": InviteAccessModal,
  "billing-modal": BillingModalContent,
  "overwrite-template": OverwriteTemplateModal,
  "delete-account": DeleteAccountModal,
};

const ModalContent: React.FC<ModalProps> = ({ className }) => {
  const { data, actions } = useZStore();
  const { status } = useSession();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const isWebsitePage = !pathname.startsWith("/dashboard");
  const isDashboardPageModal = ![
    "start-free-trial",
    "sign-up",
    "sign-in",
    "invite-user",
    "manage-consent",
    "forgot-password",
    "reset-password",
    "invite-access",
    "billing-modal",

  ].includes(data.modal.modalName || "");

  const modalName = searchParams.get("modal");
  const inviteToken = searchParams.get("inviteToken");

  useEffect(() => {
    if (modalName) {
      // If user is on a dashboard page, don't open sign-in/sign-up modals
      // They should only open on website pages
      const isAuthModal = ["sign-in", "sign-up"].includes(modalName);
      const isDashboard = pathname.startsWith("/dashboard");

      if (isAuthModal && isDashboard) {
        // On dashboard pages, auth modals should never open
        // If status is loading or authenticated, clear the param
        console.log("[Modal Wrapper] Preventing auth modal on dashboard page:", {
          modalName,
          pathname,
          status
        });

        const params = new URLSearchParams(searchParams.toString());
        params.delete("modal");
        const queryString = params.toString();
        const newPath = `${pathname}${queryString ? `?${queryString}` : ""}`;
        router.replace(newPath);
        return;
      }

      console.log("[Modal Wrapper] Opening modal:", modalName);
      actions.openModal({ modalName });
    }
  }, [searchParams, actions, modalName, pathname, status, router]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    /*
     * On dashboard pages we lock the custom scroll container (#dashboard-main-scroll).
     * On website pages that container doesn't exist, so we fall back to the
     * root scrolling element (<html>). This guarantees scroll locking everywhere.
     */
    const mainEl =
      document.getElementById("dashboard-main-scroll") ||
      document.documentElement;

    const prevOverflow = mainEl.style.overflow;

    if (data.modal.isOpen) {
      mainEl.style.overflow = "hidden";
    } else {
      mainEl.style.overflow = prevOverflow || "";
    }

    // Cleanup: restore overflow on unmount
    return () => {
      mainEl.style.overflow = prevOverflow || "";
    };
  }, [data.modal.isOpen]);
  useEffect(() => {
    // Update the URL query parameters when the modal changes
    const params = new URLSearchParams(searchParams.toString());

    if (
      data.modal.isOpen &&
      ["sign-in", "sign-up"].includes(data.modal.modalName || "")
    ) {
      // Add or update the `modal` parameter in the URL
      params.set("modal", data.modal.modalName || "");
    } else if (!data.modal.isOpen) {
      // Remove the `modal` parameter if no modal is open
      params.delete("modal");
    }

    // Construct the new URL
    const queryString = params.toString();
    const newPath = `${pathname}${queryString ? `?${queryString}` : ""}`;

    // Replace the current URL without reloading the page
    router.replace(newPath);
  }, [data.modal.isOpen, data.modal.modalName, pathname, router, searchParams]);
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      if (data.modal.modalName === "mailchimp-api-key") {
        actions.setMailchimpApiKeyModalClosed(true);
      }
      actions.closeModal();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (data.modal.modalName === "mailchimp-api-key") {
          actions.setMailchimpApiKeyModalClosed(true);
        }
        actions.closeModal();
      }
    };

    if (data.modal.isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [data.modal.isOpen, actions]);

  if (!data.modal.isOpen) return null;

  const ModalComponent = data.modal.modalName
    ? modalComponents[data.modal.modalName || ""]
    : null;



  const isBillingModal = data.modal.modalName === "billing-modal";

  return (
    <div
      onClick={handleBackdropClick}
      role="dialog"
      className={cn(
        "absolute left-0 top-0 z-40 flex h-full  w-full items-center justify-center overflow-auto custom-scrollbar bg-black/[40%]",
        // "top-[80px] xl:top-[120px]",
        // data.sideBarVisible
        //   ? "left-[75px] xl:left-[103px]"
        //   : "left-[219px] xl:left-[345px]",
        className,
        {
          "items-start": isBillingModal,
          "items-center": !isBillingModal,
          hidden: isWebsitePage && isDashboardPageModal,
        },
      )}
    >
      {data.modal.content && data.modal.modalName == null && (
        <div className="">{data.modal.content}</div>
      )}
      {ModalComponent && (!isWebsitePage || !isDashboardPageModal) && (
        <ModalComponent {...data.modal.modalProps} inviteToken={inviteToken} />
      )}
    </div>
  );
};

const Modal: React.FC<ModalProps> = (props) => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ModalContent {...props} />
    </Suspense>
  );
};

export default Modal;
